import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  BROWSE_SOUND_EFFECTS, type BrowseSoundEffectsData, type BrowseSoundEffectsVars,
  type SoundEffectRow,
} from '@/queries/soundEffects';

interface BrowseState {
  key: string;
  items: SoundEffectRow[];
  total: number;
  hasMore: boolean;
  loading: boolean;
  error: boolean;
}
const empty: BrowseState = { key: '', items: [], total: 0, hasMore: false, loading: false, error: false };

export function useSoundEffectsBrowse({ open, search, category, region, locale }: {
  open: boolean; search: string; category: string; region: 'br' | 'us' | ''; locale: string;
}) {
  const client = useApolloClient();
  const query = search.trim();
  const [debounced, setDebounced] = useState(query);
  const [state, setState] = useState<BrowseState>(empty);
  const key = JSON.stringify([query, category, region, locale]);
  const latest = useRef({ key, open });
  latest.current = { key, open };
  const sequence = useRef(0);
  const busy = useRef<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const fetchPage = useCallback(async (offset: number) => {
    if (!open || query !== debounced || busy.current === key) return;
    const request = ++sequence.current;
    busy.current = key;
    setState(prev => offset ? { ...prev, loading: true, error: false } : { ...empty, key, loading: true });
    try {
      const { data, errors } = await client.query<BrowseSoundEffectsData, BrowseSoundEffectsVars>({
        query: BROWSE_SOUND_EFFECTS,
        variables: { query: query || null, category: category || null, region: region || null, locale, limit: 20, offset },
        fetchPolicy: 'no-cache',
      });
      if (request !== sequence.current || latest.current.key !== key || !latest.current.open) return;
      if (errors?.length || !data?.browseSoundEffects) throw new Error('catalog_unavailable');
      const page = data.browseSoundEffects;
      setState(prev => {
        const items = offset ? [...prev.items] : [];
        const seen = new Set(items.map(item => item.id));
        page.items.forEach(item => { if (!seen.has(item.id)) { seen.add(item.id); items.push(item); } });
        return { key, items, total: page.total, hasMore: page.has_more, loading: false, error: false };
      });
    } catch {
      if (request !== sequence.current || latest.current.key !== key || !latest.current.open) return;
      setState(prev => ({ ...prev, key, loading: false, error: true }));
    } finally {
      if (request === sequence.current) busy.current = null;
    }
  }, [client, open, query, debounced, category, region, locale, key]);

  useEffect(() => {
    busy.current = null;
    if (open && query === debounced) void fetchPage(0);
    return () => { sequence.current += 1; busy.current = null; };
  }, [open, query, debounced, fetchPage]);

  const current = state.key === key && query === debounced;
  return {
    items: current ? state.items : [], total: current ? state.total : 0,
    loading: open && (!current || state.loading), error: current && state.error,
    hasMore: current && state.hasMore,
    loadMore: () => fetchPage(state.items.length), retry: () => fetchPage(state.items.length ? state.items.length : 0),
  };
}
